import { useState } from "react";
import logoMark from "@/assets/sentinel-crop-mark.png";

interface InteractiveLogoProps {
  size?: number;
  className?: string;
}

/**
 * Interactive Sentinel Crop logo mark.
 * - Idle: soft emerald glow pulse + slow rotation orbit ring
 * - Hover: logo lifts & tilts, orbit ring speeds up, "scanning" sweep beam
 * - Active/click: quick bounce
 * Pure CSS/Tailwind animations — respects prefers-reduced-motion.
 */
const InteractiveLogo = ({ size = 48, className = "" }: InteractiveLogoProps) => {
  const [hovered, setHovered] = useState(false);

  return (
    <span
      className={`relative inline-flex items-center justify-center group ${className}`}
      style={{ width: size, height: size }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Ambient glow */}
      <span
        aria-hidden="true"
        className={`absolute inset-0 rounded-full transition-all duration-500 ${
          hovered
            ? "bg-emerald-400/30 blur-xl scale-110"
            : "bg-emerald-400/10 blur-md scale-100 motion-safe:animate-pulse"
        }`}
      />

      {/* Orbit ring */}
      <span
        aria-hidden="true"
        className={`absolute inset-[-4px] rounded-full border border-emerald-400/30 border-dashed motion-safe:animate-[spin_12s_linear_infinite] transition-opacity duration-300 ${
          hovered ? "opacity-100" : "opacity-0"
        }`}
      />

      {/* Scanning sweep beam (visible on hover) */}
      <span
        aria-hidden="true"
        className={`absolute inset-0 rounded-full overflow-hidden pointer-events-none transition-opacity duration-300 ${
          hovered ? "opacity-100" : "opacity-0"
        }`}
      >
        <span className="absolute inset-0 motion-safe:animate-[spin_2.4s_linear_infinite]">
          <span
            className="absolute top-1/2 left-1/2 h-[2px] w-1/2 origin-left rounded-full"
            style={{
              background:
                "linear-gradient(90deg, rgba(16,185,129,0) 0%, rgba(52,211,153,0.85) 100%)",
              transform: "translateY(-50%)",
              boxShadow: "0 0 8px rgba(16,185,129,0.7)",
            }}
          />
        </span>
      </span>

      {/* Logo image */}
      <img
        src={logoMark}
        alt=""
        aria-hidden="true"
        width={size}
        height={size}
        decoding="async"
        className={`relative z-10 object-contain transition-transform duration-300 ease-out
          group-hover:-translate-y-0.5 group-hover:scale-[1.06] group-active:scale-95
          ${hovered ? "drop-shadow-[0_4px_14px_rgba(16,185,129,0.45)]" : ""}`}
        style={{ width: size, height: size }}
      />

      {/* Corner ping (satellite blip) */}
      <span
        aria-hidden="true"
        className={`absolute top-0 right-0 h-1.5 w-1.5 rounded-full bg-emerald-400 transition-opacity duration-300 ${
          hovered ? "opacity-100 motion-safe:animate-ping" : "opacity-0"
        }`}
      />
    </span>
  );
};

export default InteractiveLogo;